import { inject, Injectable } from "@angular/core";
import { AccountService } from "@app/core";
import { COMMUNICATOR_CONFIG_TOKEN, CommunicatorConfig } from "cfs-communication-pack";
import * as Pubnub from "pubnub";
import { ListenerParameters } from "pubnub";
import { EMPTY, Observable } from "rxjs";
import { distinctUntilChanged, filter, map, share, switchMap } from "rxjs/operators";

export enum AccountMessageEvent {
  PosMarketConnectionStatusChanged = "PosMarketConnectionStatusChanged"
}

interface AccountMessage {
  event?: string;
  Event?: string;
}

@Injectable({ providedIn: "root" })
export class AccountMessagesService {
  private readonly accountService = inject(AccountService);
  private readonly config = inject<CommunicatorConfig>(COMMUNICATOR_CONFIG_TOKEN);

  private readonly messages$: Observable<AccountMessage> = this.accountService.userChanged$.pipe(
    map((user) => (this.accountService.isLoggedIn ? user?.accountId ?? null : null)),
    distinctUntilChanged(),
    switchMap((accountId) => (accountId ? this.listen(accountId) : EMPTY)),
    share()
  );

  on(event: AccountMessageEvent): Observable<void> {
    return this.messages$.pipe(
      filter((message) => (message?.event ?? message?.Event) === event),
      map(() => undefined)
    );
  }

  private listen(accountId: number): Observable<AccountMessage> {
    return new Observable<AccountMessage>((subscriber) => {
      const channel = `account_message-${accountId}`;
      const pubnub = new Pubnub({
        subscribeKey: this.config.subscribeKey,
        userId: `${this.config.uuid}-${accountId}-${this.accountService.userId}`,
        ssl: true,
        logVerbosity: false,
        restore: true
      });
      const listener: ListenerParameters = {
        message: ({ channel: messageChannel, message }) => {
          if (messageChannel === channel) {
            subscriber.next(message as AccountMessage);
          }
        }
      };

      pubnub.addListener(listener);
      pubnub.subscribe({ channels: [channel] });

      return () => {
        pubnub.removeListener(listener);
        pubnub.unsubscribeAll();
        pubnub.stop();
      };
    });
  }
}
